import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import Button from '../../../../../components/button';
import CustomHeader from '../../../../../components/mazraeKit/headers/homeHeader';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import usePatchBulkStoreAdvertisement from '../../../../../services/hookService/patchBulkStoreAdvertisement/usePatchBulkStoreAdvertisement';
import {ceilToNextThousand} from '../../../../../utilities/helper/unitConvertor/roundLastThreeToZero';
import AdvertisementBulkEditableCard, {
  ISelectedAds,
} from './components/AdvertisementBulkEditableCard';

type LocationState = {
  selectedAdsData: ISelectedAds[];
  percentage: number;
};

const PreviewAndEditAdPricePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as LocationState | null;

  if (!state?.selectedAdsData) {
    navigate(-1);
    return null;
  }

  const [ads, setAds] = useState<ISelectedAds[]>(state?.selectedAdsData);
  const [isEditingMode, setIsEditingMode] = useState<boolean>(false);

  const updatePrice = (id: string, price: number) => {
    setAds(prev =>
      prev.map(ad => (ad.id === id ? {...ad, editedPrice: price} : ad)),
    );
  };

  const resolvePrice = (ad: ISelectedAds) => {
    if (ad.editedPrice != null) {
      return ad.editedPrice;
    }

    // increase original price
    const increasedOriginalPrice = Math.round(
      ad.original_price * (1 + state.percentage / 100),
    );

    // no discount
    if (!ad.discount_amount) {
      return ceilToNextThousand(increasedOriginalPrice);
    }

    // apply discount
    const discountedPrice =
      increasedOriginalPrice * (1 - ad.discount_amount / 100);

    return ceilToNextThousand(discountedPrice);
  };

  const {mutate: bulkEdit, isLoading: isBulkEditing} =
    usePatchBulkStoreAdvertisement(() => {
      toast.success('تغییرات با موفقیت ثبت شد.');
      navigate('/manage-my-store-ads?tab=published');
    });

  const applyBulkEdit = () => {
    const percentageValue = Number(state.percentage);

    if (!ads?.length) return;

    const payload = ads.map(ad => {
      let finalPrice = 0;

      // manual edited price
      if (ad.editedPrice != null) {
        finalPrice = ad.editedPrice;
      } else {
        // percentage increase
        const increasedPrice =
          ad.ad_price + ad.ad_price * (percentageValue / 100);

        finalPrice = increasedPrice;
      }

      // reverse discount
      if (ad.discount_amount) {
        finalPrice = (100 * finalPrice) / (100 - ad.discount_amount);
      }

      return {
        id: ad.id,
        ad_price: ceilToNextThousand(finalPrice),
      };
    });

    bulkEdit(payload);
  };

  return (
    <div className="flex flex-col h-full">
      <CustomHeader
        title="ویرایش چند آگهی"
        preComponent={() => <div />}
        postComponent={() => (
          <i
            className="text-2xl icon-nameClose-remove"
            onClick={() => navigate(-1)}
          />
        )}
      />

      <section className="p-3 space-y-3">
        {ads.map(ad => (
          <AdvertisementBulkEditableCard
            key={ad.id}
            ad={ad}
            newPrice={resolvePrice(ad)}
            increasePercentage={state?.percentage}
            onPriceChange={price => updatePrice(ad.id, price)}
            setIsEditingMode={setIsEditingMode}
          />
        ))}
      </section>

      <div className="sticky bottom-0 px-4 py-4 mt-auto bg-white shadow-md">
        <Button
          disabled={isEditingMode}
          className="w-full disabled:border-gray-500 disabled:opacity-30"
          onClick={applyBulkEdit}
        >
          {isBulkEditing ? <SpinnerLoading size={24} /> : 'ثبت'}
        </Button>
      </div>
    </div>
  );
};

export default PreviewAndEditAdPricePage;
